<script setup>
import BerufField from './fields/BerufField.vue'
import BrancheField from './fields/BrancheField.vue'
import EducationField from './fields/EducationField.vue'
import PLZField from './fields/PLZField.vue'
import PositionField from './fields/PositionField.vue'
import { useLohndatenStore, useResultStore } from '@/stores/lohndaten'

import { useRouter } from 'vue-router'
import { submitLohnrechner } from '@/lohnrechner'
import { inject, ref, watch } from 'vue'
import ProgressIndicator from './ProgressIndicator.vue'
import IntegerSliderField from './fields/IntegerSliderField.vue'
import posthog from 'posthog-js'
import { onMounted } from 'vue'
import { nextTick } from 'vue'
import { next } from 'locutus/php/array'

const router = useRouter()

const store = useLohndatenStore()
const resultStore = useResultStore()

async function onSubmit(FormData, form$) {
  posthog.capture('form_submitted')
  form$.messageBag.clear()

  await submitLohnrechner(store, resultStore)
  if (resultStore.state == 'error') {
    Object.keys(resultStore.errors).forEach((key) => {
      form$.el$(key).messageBag.append(resultStore.errors[key])
    })
    return
  }
  router.push({ path: '/result' })
}

const t = inject('t')

function onChange(option, old, el) {
  store.contact[el.name] = option
}

const experienceRef$ = ref(null)

let lastAge = store.age

store.$subscribe(() => {
  if (store.age != lastAge) {
    lastAge = store.age
    store.experience = Math.min(store.age - 18, store.experience || 0)
    // experienceRef$.value.updateItems()
  }
})

const value = ref(33)
const total = ref(8)
const formSteps = ref(null)

function onStepChange(active, previous) {
  posthog.capture('step_completed', {
    previous: previous.index,
    active: active.index
  })
  if (previous.index == undefined) {
    document.body.classList.remove('reverse')
  } else if (active.index > previous.index) {
    document.body.classList.remove('reverse')
  } else {
    document.body.classList.add('reverse')
  }
  value.value = active.index
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

onMounted(() => {
  const searchParams = new URLSearchParams(window.location.search)
  const job_id =
    searchParams.get('beruf') ||
    searchParams.get('profession') ||
    searchParams.get('professione') ||
    searchParams.get('job')
  if (job_id) {
    store.beruf = job_id
    nextTick(() => {
      formSteps.value.goTo('branche')
    })
  }

})

function moveToLatestStep() {
  let index = getNextUnfilledStep();
  formSteps.value.goTo(index)
}

const JOB_SELECT = 0
const BRANCHE_SELECT = 1
const ORT_SELECT = 2
const ALTER_SELECT = 3
const DIENSTALTER = 4
const POSITION_SELECT = 5
const AUSBILDUNG_SELECT = 6
const STUNDEN_SELECT = 7


function nextStep(source) {
  const currentStep = formSteps.value.current$

  if (source != currentStep.index) {
    return;
  }
  formSteps.value.current$.validate()
  formSteps.value.next()
}

function getNextUnfilledStep() {
  if (store.beruf == null) {
    return 'beruf'
  }
  if (store.branche == null) {
    return 'branche'
  }
  if (store.onrp == null) {
    return 'Ort'
  }
  if (store.age == null) {
    return 'Alter'
  }
  if (store.experience == null) {
    return 'Erfahrung'
  }
  if (store.stellung == null) {
    return 'Position'
  }
  if (store.ausbildung == null) {
    return 'Ausbildung'
  }
  if (store.stunden == null) {
    return 'Stunden'
  }
  return 'contact'
}

function canMoveForward() {
  if (!formSteps.value || !formSteps.value.current$) return false
  switch (formSteps.value.current$.index) {
    case 0:
      return store.beruf != null
    case 1:
      return store.branche != null
    case 2:
      return store.onrp != null
    case 3:
      return true
    case 4:
      return true
    case 5:
      return store.stellung != null
    case 6:
      return store.ausbildung != null
  }
  return true
}


function emailProvided() {
  return (
    store.contact.email != null && store.contact.email != '' && store.contact.email.indexOf('@') > 0
  )
}
watch(formSteps, () => {
  total.value = formSteps.value.steps$Array.length
})
</script>
<template>
  <div class="progress" v-if="formSteps && formSteps.current$.index > 0">
    <ProgressIndicator :total="total - 1" :value="value"></ProgressIndicator>
  </div>
  <Vueform id="lr-form" :add-class="'steps'" :form-data="(form$) => form$.data"
    :endpoint="async (data, form) => onSubmit(data, form)" :validate-on="'step'" :scroll-on-next="false">
    <template #empty>
      <FormSteps @select="onStepChange" ref="formSteps" :scrollOnNext="false">
        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="beruf" label=""
          :elements="['beruf', 'lohn-title']" :labels="{ next: 'Lohn berechnen' }" :buttons="{ previous: false }">
          <template #next> </template>
        </FormStep>
        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="branche" label=""
          :elements="['branche', 'branche-hint']" :labels="{ previous: 'Zurück', next: 'Weiter' }">
        </FormStep>
        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="Ort" label="" :elements="['plz']"
          :labels="{ next: 'Weiter', previous: 'Zurück' }">
        </FormStep>

        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="Alter" label="" :elements="['age']"
          :labels="{ next: 'Weiter', previous: 'Zurück' }"></FormStep>

        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="Erfahrung" label=""
          :elements="['experience']" :labels="{ next: 'Weiter', previous: 'Zurück' }"></FormStep>

        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="Position" label=""
          :elements="['isLead', 'position']" :labels="{ next: 'Weiter', previous: 'Zurück' }"></FormStep>

        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="Ausbildung" label=""
          :elements="['education']" :labels="{ next: 'Weiter', previous: 'Zurück' }"></FormStep>

        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="Stunden" label=""
          :elements="['stunden']" :labels="{ next: 'Weiter', previous: 'Zurück' }"></FormStep>

        <FormStep :add-class="'step-indicator'" :remove-class="['vf-step']" name="contact" label=""
          :elements="['closing', 'first_name', 'last_name', 'email', 'submit-button', 'privacy']"
          :labels="{ next: 'Abschicken', previous: 'Zurück' }">
        </FormStep>
      </FormSteps>
      <FormElements>
        <StaticElement name="lohn-title">
          <h1>{{ t('lohn-fair') }}</h1>
          <p class="lead">
            {{ t('intro-form') }}
          </p>
        </StaticElement>
        <BerufField @complete="nextStep(JOB_SELECT)"></BerufField>
        <BrancheField :update-hours="true" @complete="nextStep(BRANCHE_SELECT)"></BrancheField>
        <PLZField @complete="nextStep(ORT_SELECT)"></PLZField>
        <IntegerSliderField :placeholder="t('alter-frage')" :label="t('alter-frage')" name="age" :min="18" :max="64"
          :suffix="t('jahre')" @complete="nextStep(ALTER_SELECT)">
        </IntegerSliderField>
        <IntegerSliderField ref="experienceRef$" :label="t('berufserfahrung-frage')" name="experience"
          :suffix="t('jahre')" :suffix-singular="t('jahr')" :label-zero="t('weniger-als-ein-jahr')" :min="0"
          :max="store.age - 18" :placeholder="t('berufserfahrung-frage')" @complete="nextStep(DIENSTALTER)">
        </IntegerSliderField>
        <PositionField @complete="nextStep(POSITION_SELECT)"></PositionField>
        <EducationField @complete="nextStep(AUSBILDUNG_SELECT)"></EducationField>
        <IntegerSliderField :label="t('pensum-frage')" name="stunden" :suffix="t('stunden')"
          :placeholder="t('pensum-frage')" :min="8" :max="50" :step-size="0.5" @complete="nextStep(STUNDEN_SELECT)"
          :total="store.stundenTotal ?? 0">
        </IntegerSliderField>
        <StaticElement name="closing">
          <span class="bold"> {{ t('los') }} </span>
        </StaticElement>
        <TextElement @change="onChange" :default="store.contact['first_name']" :placeholder="t('vorname')"
          :label="t('vorname')" name="first_name"></TextElement>
        <TextElement @change="onChange" :default="store.contact['last_name']" :placeholder="t('nachname')"
          :label="t('nachname')" name="last_name"></TextElement>
        <TextElement @change="onChange" :default="store.contact['email']" :placeholder="t('email')" :label="t('email')"
          name="email"></TextElement>
        <ButtonElement name="submit-button" :submits="true"
          :add-class="emailProvided() ? ['submit-button'] : ['submit-button', 'inactive']">
          <div class="first-button">
            <span> {{ t('resultat-button') }} </span>
            <svg width="44" height="44" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M37.958 26.395L26.9893 37.364L16.0204 26.395" stroke="#e2001a" stroke-width="3.04252" />
              <line x1="26.9817" y1="35.7695" x2="26.9817" y2="16.7395" stroke="#e2001a" stroke-width="3.04252" />
            </svg>
          </div>
        </ButtonElement>
        <StaticElement name="privacy">
          <span class="small">
            {{ t('datenschutz') }}
            <RouterLink :to="{ name: 'flat-page', params: { slug: 'privacy' } }">
              {{ t('datenschutz-link') }}
            </RouterLink>
          </span>
        </StaticElement>
      </FormElements>
      <FormStepsControls :labels="false" :class="formSteps && formSteps.current$.index == 0 ? 'first' : 'later'">
        <template #next>
          <div v-if="formSteps && formSteps.current$.index == 0" class="first-button">
            <span> {{ t('lohn-berechnen') }} </span>
            <svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M37.958 26.395L26.9893 37.364L16.0204 26.395" stroke="#e2001a" stroke-width="3.04252" />
              <line x1="26.9817" y1="35.7695" x2="26.9817" y2="16.7395" stroke="#e2001a" stroke-width="3.04252" />
            </svg>
          </div>

          <div v-if="formSteps && formSteps.current$.index > 0 && formSteps.current$.index < total - 1">
            <svg width="54" height="54" viewBox="0 0 54 54" :class="{ active: canMoveForward() }" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M37.958 26.395L26.9893 37.364L16.0204 26.395" stroke-width="3.04252" />
              <line x1="26.9817" y1="35.7695" x2="26.9817" y2="16.7395" stroke-width="3.04252" />
            </svg>
          </div>
        </template>
        <template #previous>
          <div v-if="formSteps && formSteps.current$.index > 0 && formSteps.current$.index < total - 1">
            <svg width="54" height="54" class="active" viewBox="0 0 54 54" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M16.4014 27.9473L27.3701 16.9783L38.339 27.9472" stroke-width="3.04252" />
              <line x1="27.3777" y1="18.5723" x2="27.3777" y2="37.6023" stroke-width="3.04252" />
            </svg>
          </div>
        </template>
        <template #finish> </template>
      </FormStepsControls>
    </template>
  </Vueform>
  <div class="info" v-show="formSteps && formSteps.current$.index == 0">
    {{ t('daten') }}
  </div>
</template>

<style scoped>
.bold {
  display: block;
}

form.steps {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'form' 'controls';
  grid-template-rows: auto 1fr;
  flex: 1 1 auto;
  max-width: 720px;
  width: 100%;
  grid-area: form;
}

.steps h1 {
  font-size: 2.8rem;
  line-height: 1;
}

.info {
  font-size: 0.8rem;
  font-weight: 400;
  font-style: italic;
  text-align: center;
  line-height: 159%;
  color: var(--vf-color-floating);
  margin-top: 1rem;
  grid-area: info;
  align-self: end;
}

.inactive {
  --color-signal: #afaeae;
}

.inactive svg path,
.inactive svg line {
  stroke: var(--color-signal);
}
</style>

<style>
.submit-button .vf-layout-inner-wrapper {
  margin-top: 0;
}

.inactive button.vf-btn.vf-btn.vf-btn:hover {
  transform: scale(1);
}

#lr-form .vf-steps-controls.later .vf-btn.vf-btn-primary {
  pointer-events: none;
}

#lr-form .vf-steps-controls.later .vf-btn.vf-btn-primary .active {
  pointer-events: all;
}
</style>
